import { Component, OnInit, inject, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { take } from 'rxjs';
import { EditDialogInstance } from '../editDialog/editDialogService';
import { EditDialogComponent } from '../editDialog/editDialogComponent';
import { IssueInput } from '../../entities/issue/issueInput';
import { IssueType } from '../../entities/issue/issue-type';
import { IssueTypeService } from '../../services/issue/issue-type.service';
import { EditSelectable } from '../../entities/formControls/edit-selectable';

/**
 * Entity Link Configuration Interface
 */
interface EntityLinkConfig {
  role: string;              // Used as FormControl name AND as role in EntityLinkInput
  linkedEntityType: string;
  linkType: string;
  label: string;
  placeholder: string;
  multiple: boolean;         // single vs multi widget
  required?: boolean;
}

/**
 * Issue Type Configuration
 */
interface IssueTypeConfig {
  entityLinks: EntityLinkConfig[];
}

@Component({
  // tslint:disable-next-line:component-selector
  selector: "app-issue-create-dialog",
  templateUrl: "issue-create-dialog.html",
  styleUrls: ["./issue-create-dialog.scss"]
})
export class IssueCreateDialogComponent implements EditDialogInstance, OnInit {

  public issueFormGroup: FormGroup;
  issueTypes: IssueType[] | undefined;
  issueTypeOptions: EditSelectable[] | undefined;

  // Current entity link configs for the selected issue type (used in template)
  currentEntityLinkConfigs: EntityLinkConfig[] = [];

  private readonly destroyRef = inject(DestroyRef);

  // Configuration for all issue types
  private readonly issueTypeConfigs = new Map<string, IssueTypeConfig>([
    ['generic', {
      entityLinks: [
        {
          role: 'order',
          linkedEntityType: 'Order',
          linkType: 'RELATED_TO',
          label: 'Auftrag',
          placeholder: 'Auftrag auswählen',
          multiple: false
        },
        {
          role: 'customer',
          linkedEntityType: 'Contact',
          linkType: 'RELATED_TO',
          label: 'Kunde',
          placeholder: 'Kunde auswählen',
          multiple: false
        }
      ]
    }],
    ['damage', {
      entityLinks: [
        {
          role: 'order',
          linkedEntityType: 'Order',
          linkType: 'RELATED_TO',
          label: 'Auftrag',
          placeholder: 'Auftrag auswählen',
          multiple: false
        },
        {
          role: 'customer',
          linkedEntityType: 'Contact',
          linkType: 'RELATED_TO',
          label: 'Kunde',
          placeholder: 'Kunde auswählen',
          multiple: false
        },
        {
          role: 'resource',
          linkedEntityType: 'Resource',
          linkType: 'RELATED_TO',
          label: 'Ressource',
          placeholder: 'Ressource auswählen',
          multiple: false
        }
      ]
    }]
  ]);

  constructor(
    public ref: DynamicDialogRef,
    public config: DynamicDialogConfig,
    private readonly issueTypeService: IssueTypeService,
  ) {
    // Initialize form in constructor to ensure it's available for getEditForm()
    // which may be called from parent's ngAfterViewInit
    this.initForm();
  }

  ngOnInit(): void {
    this.loadIssueTypes();
  }

  private loadIssueTypes(): void {
    this.issueTypeService.getIssueTypes(true).pipe(
      take(1)
    ).subscribe((issueTypes) => {
      this.issueTypes = issueTypes;
      this.issueTypeOptions = issueTypes.map((issueType) => {
        return {
          name: issueType.name,
          id: issueType.code
        } as EditSelectable;
      });
    });
  }

  private initForm(): void {
    this.issueFormGroup = new FormGroup({
      type: new FormControl(undefined, [Validators.required]),
      title: new FormControl(undefined, [Validators.required]),
      description: new FormControl(undefined, [Validators.required]),
      labels: new FormControl([]),
      location: new FormControl(undefined),
    });

    // Watch for type changes and add/remove controls accordingly
    this.issueFormGroup.get('type')?.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((typeCode) => {
        this.updateFormControlsForIssueType(typeCode);
      });
  }

  /**
   * Updates form controls based on selected issue type using configuration
   */
  private updateFormControlsForIssueType(typeCode: string): void {
    // Remove all dynamic controls from previous type
    this.currentEntityLinkConfigs.forEach(config => {
      if (this.issueFormGroup.contains(config.role)) {
        this.issueFormGroup.removeControl(config.role);
      }
    });

    // Clear configs first to let Angular remove components from DOM
    this.currentEntityLinkConfigs = [];

    // Get configuration for new type and add controls in next tick
    // This ensures old components are destroyed before new controls are added
    setTimeout(() => {
      const config = this.issueTypeConfigs.get(typeCode);
      this.currentEntityLinkConfigs = config?.entityLinks || [];

      // Add new controls based on configuration
      this.currentEntityLinkConfigs.forEach(linkConfig => {
        const initialValue = linkConfig.multiple ? [] : null;
        const validators = linkConfig.required ? [Validators.required] : [];

        this.issueFormGroup.addControl(
          linkConfig.role,
          new FormControl(initialValue, validators)
        );
      });
    }, 0);
  }

  onConfirm(editDialogComponent: EditDialogComponent<any>): void {
    const issueInput = new IssueInput();
    issueInput.typeCode = this.issueFormGroup.get('type')?.value;
    issueInput.title = this.issueFormGroup.get('title')?.value;
    issueInput.description = this.issueFormGroup.get('description')?.value;
    issueInput.locationId = this.issueFormGroup.get('location')?.value || null;

    // Collect labels
    issueInput.labels = this.issueFormGroup.get('labels')?.value || [];

    // Collect entity links dynamically based on current configuration
    const entityLinks = [];

    this.currentEntityLinkConfigs.forEach(config => {
      const value = this.issueFormGroup.get(config.role)?.value;

      if (value) {
        if (config.multiple && Array.isArray(value)) {
          // Multi widget returns array - spread it
          entityLinks.push(...value);
        } else if (!config.multiple && value !== null) {
          // Single widget returns single value
          entityLinks.push(value);
        }
      }
    });

    issueInput.entityLinks = entityLinks.length > 0 ? entityLinks : undefined;

    this.ref.close(issueInput);
  }

  onReject(editDialogComponent: EditDialogComponent<any>): void {
    this.ref.close(false);
  }

  getEditForm(): FormGroup {
    return this.issueFormGroup;
  }

}
